import { cashPlanSheet } from "../../../shared/account-finance/cash-plan-domain";
import type { CashPlans } from "../../../shared/account-finance/cash-plan-contract";
import type { WorkbookCoverage, WorkbookDashboard, WorkbookMetadata, WorkbookSheet } from "../../../shared/account-finance/finance-workbook-contract";

export function assertWorkbookScope(value: { date: string; company: string; sheets?: readonly WorkbookSheet[] },
  date: string, company?: string | null) {
  if (value.date !== date || (company && value.company !== company)
    || value.sheets?.some(sheet => sheet.date !== value.date || sheet.company !== value.company))
    throw new Error("Finans yanıtının şirket veya tarih kapsamı eşleşmiyor.");
}

export const workbookSheetCacheKey = (company: string, date: string, id: string, page: number) => `${company}:${date}:${id}:${page}`;
const sameScope = (left: WorkbookDashboard, right: { date: string; company: string }) => left.date === right.date && left.company === right.company;
const manualId = "manuel-plan";
const failedStatus = (status?: string) => ["failed", "deferred", "partial", "not_read"].includes(status ?? "");

function metadataWithManualSource(metadata: WorkbookMetadata | undefined, source: WorkbookMetadata["sources"][number] | undefined) {
  if (!metadata || !source) return metadata;
  return { ...metadata, sources: [...metadata.sources.filter(item => item.id !== manualId), source] };
}

export function applyCashPlansToWorkbook(previous: WorkbookDashboard | null, plans: CashPlans): WorkbookDashboard | null {
  if (!previous || previous.company !== plans.company || plans.revision < (previous.planRevision ?? -1)) return previous;
  let sheet: WorkbookSheet;
  try { sheet = cashPlanSheet(plans, previous.date); }
  catch (failure) {
    const message = failure instanceof Error ? failure.message : "Plan takvimi hesaplanamadı.";
    const existing = previous.sheets.find(item => item.id === manualId);
    return { ...previous, planRevision: plans.revision, cashAvailability: plans.cashAvailability ?? [],
      errors: [...previous.errors.filter(item => item.id !== manualId), { id: manualId, message }],
      metadata: metadataWithManualSource(previous.metadata, { id: manualId, origin: "user", status: "failed",
        observedAt: existing?.observedAt ?? null, recordCount: existing?.rows.length ?? null, message }) };
  }
  return { ...previous, planRevision: plans.revision, cashAvailability: plans.cashAvailability ?? [],
    sheets: [...previous.sheets.filter(item => item.id !== manualId), sheet],
    errors: previous.errors.filter(item => item.id !== manualId),
    metadata: metadataWithManualSource(previous.metadata, { id: manualId, origin: "user", status: "ready",
      observedAt: sheet.observedAt, recordCount: sheet.rows.length, message: null }) };
}

// A full or source-specific response can have been read before a plan save. The
// plan revision is authoritative; source observation dates do not version plans.
export function reconcileWorkbookResponse(previous: WorkbookDashboard | null, incoming: WorkbookDashboard,
  latestPlans?: CashPlans | null): WorkbookDashboard {
  let result = incoming;
  if (previous && sameScope(previous, incoming) && previous.planRevision !== undefined
    && previous.planRevision > (incoming.planRevision ?? -1)) {
    const sheet = previous.sheets.find(item => item.id === manualId);
    const unknownFailure = incoming.planRevision === undefined ? incoming.errors.find(item => item.id === manualId) : undefined;
    const error = unknownFailure ?? previous.errors.find(item => item.id === manualId);
    const source = unknownFailure ? incoming.metadata?.sources.find(item => item.id === manualId)
      : previous.metadata?.sources.find(item => item.id === manualId);
    result = { ...incoming, planRevision: previous.planRevision, cashAvailability: previous.cashAvailability,
      sheets: [...incoming.sheets.filter(item => item.id !== manualId), ...(sheet ? [sheet] : [])],
      errors: [...incoming.errors.filter(item => item.id !== manualId), ...(error ? [error] : [])],
      metadata: metadataWithManualSource(incoming.metadata, source) };
  }
  if (latestPlans && latestPlans.company === result.company && latestPlans.revision > (result.planRevision ?? -1)) {
    return applyCashPlansToWorkbook(result, latestPlans) ?? result;
  }
  return result;
}

export function reconcileWorkbookSheet(previous: WorkbookDashboard | null, incoming: WorkbookSheet,
  metadata?: WorkbookMetadata): WorkbookDashboard | null {
  if (!previous || !sameScope(previous, incoming) || incoming.id === manualId) return previous;
  const existing = previous.sheets.find(sheet => sheet.id === incoming.id);
  if (existing && Date.parse(existing.observedAt) > Date.parse(incoming.observedAt)) return previous;
  const observation = metadata?.sources.find(source => source.id === incoming.id);
  return { ...previous, sheets: [...previous.sheets.filter(item => item.id !== incoming.id), incoming],
    errors: previous.errors.filter(item => item.id !== incoming.id),
    metadata: previous.metadata ? { ...previous.metadata, sources: previous.metadata.sources.map(item => item.id === incoming.id
      ? observation ?? { ...item, status: incoming.hasMore ? "partial" : "ready", observedAt: incoming.observedAt,
        recordCount: incoming.rows.length, message: incoming.hasMore ? "Kapsam eksik; genel nakit hesabına alınmaz." : null } : item),
    } : undefined,
  };
}

export function reconcileWorkbookCoverage(previous: WorkbookDashboard | null, incoming: {
  date: string; company: string; coverage: WorkbookCoverage[]; metadata?: WorkbookMetadata;
}): WorkbookDashboard | null {
  if (!previous || !sameScope(previous, incoming)) return previous;
  if (previous.metadata && incoming.metadata && Date.parse(incoming.metadata.servedAt) < Date.parse(previous.metadata.servedAt)) return previous;
  const failures = incoming.metadata?.sources.filter(source => source.id !== manualId && ["failed", "deferred"].includes(source.status)) ?? [];
  const failedIds = new Set(failures.map(source => source.id));
  const metadata = previous.metadata && incoming.metadata ? { ...previous.metadata,
    servedAt: incoming.metadata.servedAt, retryAfter: incoming.metadata.retryAfter,
    sources: previous.metadata.sources.map(source => {
      const failure = failures.find(item => item.id === source.id);
      return failure ? { ...source, status: failure.status, message: failure.message } : source;
    }) } : previous.metadata;
  return { ...previous, coverage: incoming.coverage, metadata,
    errors: [...previous.errors.filter(error => !failedIds.has(error.id)),
      ...failures.filter(source => source.id !== "rates").map(source => ({ id: source.id, message: source.message ?? "Kaynak yenilenemedi." }))],
    context: failedIds.has("rates") ? { ...previous.context, rates: [], expenses: previous.context?.expenses ?? [],
      errors: Array.from(new Set([...(previous.context?.errors ?? []), "rates" as const])) } : previous.context };
}

export function workbookSourceNeedsReview(book: WorkbookDashboard, id: string): boolean {
  if (book.errors.some(item => item.id === id) || failedStatus(book.metadata?.sources.find(item => item.id === id)?.status)) return true;
  if (id === "rates") return !book.context || book.context.errors.includes("rates") || ["USD", "EUR"].some(currency =>
    !book.context?.rates.some(rate => rate.currency === currency && rate.date === book.date && BigInt(rate.tlPerUnit) > 0n));
  const sheet = book.sheets.find(item => item.id === id);
  return !sheet || sheet.hasMore;
}

export function workbookRefreshNotice(book: WorkbookDashboard) {
  const partial = book.errors.length > 0 || workbookSourceNeedsReview(book, "rates")
    || book.sheets.some(sheet => sheet.hasMore)
    || book.metadata?.sources.some(source => ["failed", "deferred", "partial"].includes(source.status));
  return partial ? "Okuma tamamlandı; bazı kaynaklar eksik. Kaynak durumlarını inceleyin."
    : book.metadata?.cacheHit ? "Kayıtlı görünüm yüklendi; kaynak tarihleri değişmedi."
      : "Kaynak okuması tamamlandı. Kaynakların son okuma tarihlerini inceleyebilirsiniz.";
}
